import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IonContent,
  IonPage,
  useIonRouter,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { setActiveTab, setIsActive, Tabs } from '../../redux/tabBar/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router';
import {
  psychologistSelectorById,
  specialistSelectorPositionById,
  specialistsFiltersLoadingSelector,
} from '../../redux/specialists/selector';
import { PrivateRoutes, PublicRoutes } from 'src/routes/navigation';
import { userSelector } from '../../redux/user/selector';
import { loadConsultationsThunk } from '../../redux/consultations/action';
import { fetchSubscriptions } from '../../redux/subscriptions/thunk';
import { lastActiveSubscriptionSelector } from '../../redux/subscriptions/selector';
import useLangs from '../../context/LangsContext';
import { ratedSpecialistSelector, specialistReviewsSelector } from '../../redux/specialistReviews/selector';
import { fetchReviews, updateReview } from '../../redux/specialistReviews/thunk';
import AppointmentFooter from '../../components/AppointmentFooter';
import { resetOffset } from '../../redux/specialistReviews/reducer';
import PageHeader from '../../components/PageHeader/PageHeader';
import useToast from '../../context/ToastContext/ToastContext';
import { useFavouriteSpecialist } from '../../hooks/useFavouriteSpecialist';
import { fetchFavouriteSpecialists } from '../../redux/favouriteSpecialits/thunk';
import { fetchSpecialistByID } from '../../redux/loadedSpecialists/thunk';
import {
  TranslatedShareModalFeature,
  TranslatedSpecialistDetailWidget,
  TranslatedSpecialistDetailWithTabsWidget,
  TranslatedSpecialistProfileNotFound,
} from '../../translated-components';
import { AnalyticsEventsMapping, useAnalytics, useAuthFlow } from '../../shared';
import { DownloadAppFeature } from '../../features/DownloadAppFeature';
import { deepLink } from '../../shared/utils/deepLink';
import { useTranslation } from 'react-i18next';
import { environment } from '../../enviroments';
import {
  CardModal,
  Container_v2 as Container,
  ContractStatusEnum,
  IconButton,
  IconHeart,
  isSpecialistBlocked,
  MapStatusContractToUIStatus,
  ScheduleDate,
  ScheduleSlot,
  Specialist,
  SpecialistDetailWidgetSkeleton,
  SpecialistDetailWithTabsSkeleton,
  SpecialistReview,
  useEvent,
  useSpecialistScheduleContext,
  withSpecialistScheduleContext,
} from '@mindly/ui-components';
import { favouriteSpecialistsLoadingSelector } from 'src/redux/favouriteSpecialits/selector';
import { useSwipeBackGesture } from 'src/shared/hooks/useSwipeBackGesture';
import { fetchSpecialistsFilters } from 'src/redux/specialists/thunk';
import { useUser } from 'src/shared/hooks/useUser';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useSignAgreementsContext } from 'src/context/SignAgreementsContext/SignAgreements.context';
import { getNeedReviewData } from 'src/redux/needReview/selector';
import { useIsClientCanBookSession } from 'src/shared/hooks/useIsClientCanBookSession';
import { consultationsBySpecialistIDSelector } from 'src/redux/consultations/selector';
import { fetchSpecialistCalendar } from 'src/api/specialist/calendar';
import { NEW_SPECIALIST_ARTICLE, SPECIALIST_STATISTIC } from 'src/intercom-articles';
import { resetReviewData, setReviewData } from 'src/redux/needReview/reducer';
import { updateLoadedSpecialistBy } from 'src/redux/loadedSpecialists/reducer';
import { updateSpecialistBy } from 'src/redux/specialists/reducer';
import i18nCountries from 'i18n-iso-countries';
import { SignAgreementWidget } from 'src/widgets';
import { Review } from 'src/features/Review';
import { DateTime } from 'luxon';
import ConfirmAppointmentFeature from 'src/features/ConfirmAppointmentFeature';
import { ConfirmAppointmentFeatureRef } from 'src/features/ConfirmAppointmentFeature/types';
import { getLocalizedAreasOfWorkByClientProblemNames } from 'src/api/areasOfWork';
import useIntercomPlugin from 'src/shared/hooks/useIntercomPlugin';
interface SpecialistProfileProps
  extends RouteComponentProps<{
    id: string;
  }> {}

const SpecialistProfilePage: React.FC<SpecialistProfileProps> = ({ match }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { showToast } = useToast();
  const { locale } = useLangs();
  const { id } = match.params;
  const { authUser } = useAuthFlow();
  const { t, i18n } = useTranslation(['app', 'specialists', 'reviews']);
  const pageRef = useRef();
  const { trackPredefinedEvent } = useAnalytics();
  const { isFavouriteSpecialist, toggleFavourite } = useFavouriteSpecialist();

  const [isLoading, setIsLoading] = useState(false);
  const [isShareOpened, setIsShareOpened] = useState(false);
  const [isEventTracked, setIsEventTracked] = useState(false);
  const [isPageView, setIsPageView] = useState(false);

  const activeSubscription = useSelector(lastActiveSubscriptionSelector(id));
  const currentUser = useSelector(userSelector);
  const reviews = useSelector(specialistReviewsSelector(id));

  const specialist = useSelector(psychologistSelectorById(id));
  const specialistPosition = useSelector(specialistSelectorPositionById(id));

  const favouriteSpecialistListStatus = useSelector(favouriteSpecialistsLoadingSelector);
  const loadingFiltersState = useSelector(specialistsFiltersLoadingSelector);
  const { isNeedRedirectToOnboarding, redirectToOnboarding } = useUser();
  const isScrollHalfPage = useRef(false);
  const isScrollFullPage = useRef(false);
  const [isLoadingAreasOfWorkByClientProblem, setIsLoadingAreasOfWorkByClientProblem] = useState(false);
  const [areasOfWorkByClientProblemNames, setAreasOfWorkByClientProblemNames] = useState<Array<string>>([]);
  const contentRef = useRef<HTMLIonContentElement | null>(null);
  const newProfile = useFeatureIsOn('specialist-profile-v3');
  const isLocalizedAreasOfWorkByClientProblem = useFeatureIsOn('localized_areas_of_work_by_client_problem');

  const [isModalReviewOpen, setIsModalReviewOpen] = useState(false);
  const { isSpecialistUnAvailable } = useIsClientCanBookSession({
    specialistId: id,
    clientId: currentUser?.id!,
  });
  const consultations = useSelector(consultationsBySpecialistIDSelector(id));
  const confirmAppointmentRef = useRef<ConfirmAppointmentFeatureRef>(null);

  const {
    closeModal,
    currentOpenModal,
    openModal,
    openContractById,
    isSignAgreementOpen,
    schedulePresentingElement,
    createContractBySpecialistId,
    fetchStatus,
  } = useSignAgreementsContext();
  const reviewData = useSelector(getNeedReviewData);
  const { displayArticle } = useIntercomPlugin();

  const ratedSpecialist = useSelector(ratedSpecialistSelector);
  const {
    resetSelectedScheduleData,
    setSchedule,
    setIsLoading: setIsLoadingSchedule,
    selectedSlot,
    setSelectedSlot,
  } = useSpecialistScheduleContext();

  const navigation = useIonRouter();

  useSwipeBackGesture(pageRef);

  useIonViewDidEnter(() => {
    setIsEventTracked(false);
    setIsPageView(true);
  });

  useIonViewWillLeave(() => {
    setIsPageView(false);
  });

  const specialistShortData = useMemo(() => {
    return {
      specialist_id: id,
      specialist_name: `${specialist?.first_name} ${specialist?.last_name}`,
      specialist_order_position: specialistPosition,
    };
  }, [id, specialist, specialistPosition]);

  useEffect(() => {
    if (!isPageView) return;
    if (isEventTracked) return;
    if (!specialist) return;

    setIsEventTracked(true);
    trackPredefinedEvent('SPECIALIST_PROFILE_VIEWED', specialistShortData);
  }, [isEventTracked, isPageView, specialistShortData, trackPredefinedEvent, specialist]);

  useIonViewWillEnter(async () => {
    try {
      reviews && dispatch(resetOffset(id));
      reviews && dispatch(fetchReviews({ specialistID: id, showEditableForClientId: currentUser?.id }));

      if (!specialist) {
        setIsLoading(true);

        if (loadingFiltersState === 'idle') {
          try {
            await dispatch(fetchSpecialistsFilters({ t, isLocalizedAreasOfWorkByClientProblem })).unwrap();
          } catch (error) {
            console.log(error);
          }
        }

        await dispatch(fetchSpecialistByID(id));
      }

      await Promise.all([
        // dispatch(fetchReviews(id)),
        currentUser?.id && dispatch(loadConsultationsThunk(currentUser.id)),
        currentUser?.id && dispatch(fetchSubscriptions(currentUser.id)),
        favouriteSpecialistListStatus !== 'succeeded' && dispatch(fetchFavouriteSpecialists()),
      ]);
    } catch (error) {
      console.log(error);
      showToast({ error });
      history.push(currentUser?.id ? PrivateRoutes.PSYCHOLOGIST_LIST : PublicRoutes.AUTH);
    } finally {
      setIsLoading(false);
    }
  }, [id, specialist, activeSubscription, locale, currentUser, favouriteSpecialistListStatus, loadingFiltersState, t]);

  useIonViewWillEnter(() => {
    dispatch(setIsActive(false));
  }, []);

  useIonViewWillLeave(() => {
    if (navigation.routeInfo.pathname === PrivateRoutes.PSYCHOLOGIST_LIST) {
      dispatch(setActiveTab(Tabs.first));
      dispatch(setIsActive(true));
    }
  }, [currentUser, navigation.routeInfo.pathname]);

  const handleAppDownload = useCallback(async () => {
    if (!specialist) return;

    deepLink({ lang: i18n.language }, `s/${specialist.slug}`);
  }, [deepLink, i18n.language, specialist]);

  const loadingValue = useMemo(() => isLoading, [isLoading]);
  const showNotFound = useMemo(() => {
    return !loadingValue && (!specialist || isSpecialistBlocked(specialist as unknown as Specialist));
  }, [loadingValue, specialist]);

  const trackPercentViewEvent = useCallback(
    (eventName: keyof AnalyticsEventsMapping) => {
      trackPredefinedEvent(eventName, {
        specialist_id: id,
        specialist_name: `${specialist?.first_name} ${specialist?.last_name}`,
        specialist_order_position: specialistPosition,
      });
    },
    [id, specialist, specialistPosition, currentUser?.specialist_order, trackPredefinedEvent]
  );

  useEffect(() => {
    const ionContentEl = contentRef.current;
    const handleScroll = async () => {
      if (!ionContentEl) return;

      const scrollEl = await ionContentEl.getScrollElement();
      const scrollHeight = scrollEl.scrollHeight;
      const clientHeight = scrollEl.clientHeight;

      const scrollPercent = Math.round(((scrollEl.scrollTop + clientHeight) / scrollHeight) * 100);

      if (scrollPercent >= 50 && !isScrollHalfPage.current) {
        isScrollHalfPage.current = true;
        trackPercentViewEvent('SPECIALIST_PROFILE_SCROLL_50_PERCENT');
      }

      if (scrollPercent >= 100 && !isScrollFullPage.current) {
        isScrollFullPage.current = true;
        trackPercentViewEvent('SPECIALIST_PROFILE_SCROLL_100_PERCENT');
      }
      window.dispatchEvent(new CustomEvent('specialist-profile-scroll', { detail: { scrollEl } }));
    };

    ionContentEl?.addEventListener('ionScroll', handleScroll);

    return () => {
      isScrollHalfPage.current = false;
      isScrollFullPage.current = false;
      ionContentEl?.removeEventListener('ionScroll', handleScroll);
    };
  }, [trackPercentViewEvent]);

  const handleLoadMoreReviews = useCallback(() => {
    dispatch(fetchReviews({ specialistID: id, showEditableForClientId: currentUser?.id }));
  }, [id, currentUser?.id]);

  const clientSpecialistContractStatus = specialist?.client?.specialist_client_contract_status
    ? MapStatusContractToUIStatus[specialist?.client?.specialist_client_contract_status]
    : 'not_signed';

  const handleOpenContract = useEvent(() => {
    if (
      (clientSpecialistContractStatus === 'active' && specialist?.client?.specialist_client_contract_id) ||
      (clientSpecialistContractStatus === 'expired' && specialist?.client?.specialist_client_contract_id)
    ) {
      openContractById(specialist.client?.specialist_client_contract_id, specialist.id, i18n.language);
      return;
    }
    openModal('schedule');
  });

  const handleRateSession = useCallback(() => {
    if (specialist) {
      setIsModalReviewOpen(true);
      dispatch(setReviewData(specialist));
    }
  }, [specialist, dispatch]);

  const isShowContract = specialist?.mindly_contract_status === ContractStatusEnum.active;

  const handleApplyContract = useCallback(async () => {
    if (clientSpecialistContractStatus === 'not_signed' && specialist) {
      const data = await createContractBySpecialistId(specialist.id);
      dispatch(
        updateSpecialistBy({
          field: 'id',
          value: specialist.id,
          data: {
            client: {
              specialist_client_contract_status: data.status,
              specialist_client_contract_id: data.id,
            },
          },
        })
      );
      dispatch(
        updateLoadedSpecialistBy({
          field: 'id',
          value: specialist.id,
          data: {
            client: {
              specialist_client_contract_status: data.status,
              specialist_client_contract_id: data.id,
            },
          },
        })
      );
    }
    closeModal();
  }, [clientSpecialistContractStatus, specialist, createContractBySpecialistId, dispatch]);

  const handleReviewModalClose = useCallback(() => {
    setIsModalReviewOpen(false);
    dispatch(resetReviewData());
  }, []);

  const showReview =
    !ratedSpecialist?.find((review) => review === specialist?.id) && specialist?.profile_lang === i18n.language;
  const hasSession = !!consultations?.length;

  const handleOpenSuperSpecialistArticle = useCallback(() => {
    trackPredefinedEvent('SPECIALIST_PROFILE_SUPER_SPECIALIST_CLICKED', specialistShortData);
    displayArticle(SPECIALIST_STATISTIC);
  }, [trackPredefinedEvent, specialistShortData]);

  const handleOpenNewSpecialistArticle = useCallback(() => {
    trackPredefinedEvent('SPECIALIST_PROFILE_NEW_SPECIALIST_CLICKED', specialistShortData);
    displayArticle(NEW_SPECIALIST_ARTICLE);
  }, [trackPredefinedEvent, specialistShortData]);

  const onEditSpecialistReview = useCallback(
    async (data: SpecialistReview) => {
      try {
        await dispatch(
          updateReview({
            id: data.id,
            starsCount: data.review_rating,
            textOfReview: data.text_of_review,
          })
        );

        trackPredefinedEvent('RATING_PUBLIC_REVIEW_UPDATED', {
          specialist_id: specialist?.id,
          specialist_name: `${specialist?.first_name} ${specialist?.last_name}`,
        });

        showToast({ text: t('reviews:review_submitted_text') });
      } catch (error) {
        console.log('error', error);
        showToast({ error });
      } finally {
        setIsModalReviewOpen(false);
      }
    },
    [trackPredefinedEvent, specialist]
  );

  useIonViewWillEnter(() => {
    (async () => {
      setIsLoadingSchedule(true);
      resetSelectedScheduleData();
      try {
        const scheduleData = await fetchSpecialistCalendar(id);

        setSchedule(scheduleData);
      } catch (error) {
        console.log('error', error);
      } finally {
        setIsLoadingSchedule(false);
      }
    })();
  }, [id, resetSelectedScheduleData]);

  useIonViewWillLeave(() => {
    setSchedule(undefined);
    setIsLoadingSchedule(true);
    resetSelectedScheduleData();
  }, [id]);

  const handleOpenVideoCover = useCallback(() => {
    trackPredefinedEvent('SPECIALIST_PROFILE_VIDEO_COVER_CLICKED', specialistShortData);
  }, [trackPredefinedEvent, specialistShortData]);

  const handleOpenVideoButton = useCallback(() => {
    trackPredefinedEvent('SPECIALIST_PROFILE_VIDEO_BUTTON_CLICKED', specialistShortData);
  }, [trackPredefinedEvent, specialistShortData]);

  const handleOpenTab = useCallback(
    (tabName: string) => {
      trackPredefinedEvent('SPECIALIST_PROFILE_TAB_CLICKED', {
        ...specialistShortData,
        tab_name: tabName,
      });
    },
    [trackPredefinedEvent, specialistShortData]
  );

  const handleOpenMoreButton = useCallback(
    (entity: string) => {
      trackPredefinedEvent('SPECIALIST_PROFILE_MORE_BUTTON_CLICKED', {
        ...specialistShortData,
        block_name: entity,
      });
    },
    [trackPredefinedEvent, specialistShortData]
  );

  const handleReviewsModalViewed = useCallback(() => {
    trackPredefinedEvent('SPECIALIST_PROFILE_REVIEWS_MODAL_VIEWED', specialistShortData);
  }, [trackPredefinedEvent, specialistShortData]);

  const handleSlotsViewed = useCallback(
    (date: ScheduleDate, slots: ScheduleSlot[]) => {
      trackPredefinedEvent('SPECIALIST_PROFILE_SLOTS_VIEWED', {
        ...specialistShortData,
        available_times_slots: {
          date: DateTime.fromISO(date).toFormat('yyyy-MM-dd'),
          available_times: slots.map((slot) => DateTime.fromISO(slot).toFormat('HH:mm')),
        },
      });
    },
    [trackPredefinedEvent, specialistShortData]
  );

  const handleSlotSelected = useCallback(
    (slot: ScheduleSlot) => {
      trackPredefinedEvent('SPECIALIST_PROFILE_SLOT_SELECTED', {
        ...specialistShortData,
        date_chosen: DateTime.fromISO(slot).toFormat('yyyy-MM-dd HH:mm'),
      });
      // need to wait for update selected slot
      setTimeout(() => {
        confirmAppointmentRef.current?.handleBookingClick();
      }, 0);
    },
    [trackPredefinedEvent, specialistShortData]
  );

  const events = useMemo(() => {
    return {
      onOpenSuperSpecialistArticle: handleOpenSuperSpecialistArticle,
      onOpenNewSpecialistArticle: handleOpenNewSpecialistArticle,
      onVideoCoverClick: handleOpenVideoCover,
      onVideoButtonClick: handleOpenVideoButton,
      onTabClick: handleOpenTab,
      onMoreClick: handleOpenMoreButton,
      onReviewsModalViewed: handleReviewsModalViewed,
      onScheduleDateChange: handleSlotsViewed,
      onScheduleSlotChange: handleSlotSelected,
    };
  }, [
    handleOpenSuperSpecialistArticle,
    handleOpenNewSpecialistArticle,
    handleOpenVideoCover,
    handleOpenVideoButton,
    handleOpenTab,
    handleOpenMoreButton,
    handleReviewsModalViewed,
    handleSlotsViewed,
    handleSlotSelected,
  ]);

  const skeleton = useMemo(() => {
    return newProfile ? <SpecialistDetailWithTabsSkeleton /> : <SpecialistDetailWidgetSkeleton />;
  }, []);

  const getAreasOfWorkByClientProblemNames = useCallback(async () => {
    setIsLoadingAreasOfWorkByClientProblem(true);

    try {
      const res = await getLocalizedAreasOfWorkByClientProblemNames(
        locale,
        specialist?.localized_areas_of_work_by_client_problem ?? []
      );

      setAreasOfWorkByClientProblemNames(res);
    } catch (err) {
    } finally {
      setIsLoadingAreasOfWorkByClientProblem(false);
    }
  }, [locale, specialist]);

  const isLocalized = useMemo(() => {
    return isLocalizedAreasOfWorkByClientProblem && specialist?.localized_areas_of_work_by_client_problem;
  }, [specialist?.localized_areas_of_work_by_client_problem]);

  useEffect(() => {
    if (isLocalized) {
      getAreasOfWorkByClientProblemNames();
    }
  }, [isLocalized]);

  const areasOfWorkByClientProblem = useMemo(() => {
    return isLocalized
      ? {
          names: areasOfWorkByClientProblemNames,
          label: 'localized_areas_of_work_by_client_problem',
        }
      : {
          names: specialist?.areas_of_work_by_client_problem,
          label: 'areas_of_work_by_client_problem',
        };
  }, [areasOfWorkByClientProblemNames, specialist?.areas_of_work_by_client_problem, isLocalized]);

  const widget = useMemo(() => {
    return newProfile ? (
      <TranslatedSpecialistDetailWithTabsWidget
        specialist={
          {
            ...specialist,
            percent_match: 0,
          } as unknown as Specialist
        }
        reviews={reviews as SpecialistReview[]}
        country={i18nCountries.getName(specialist?.country_of_birthday ?? '', i18n.language) ?? ''}
        isSpecialistUnAvailable={isSpecialistUnAvailable}
        isAuth={!!authUser}
        onLoadMoreReviews={handleLoadMoreReviews}
        currentUser={currentUser as any}
        hasSessions={hasSession}
        showContract={isShowContract}
        onOpenContract={handleOpenContract}
        showReview={showReview}
        onRateSession={handleRateSession}
        onEditSpecialistReview={onEditSpecialistReview}
        isLoading={isLoading || !specialist}
        events={events}
        areasOfWorkByClientProblem={areasOfWorkByClientProblem}
        isLoadingAreasOfWorkByClientProblem={isLoadingAreasOfWorkByClientProblem}
        // TODO it's have bug with page
        // presentingElement={presentingElement}
      />
    ) : (
      <TranslatedSpecialistDetailWidget
        specialist={{ ...specialist, percent_match: 0 } as unknown as Specialist}
        reviews={reviews as SpecialistReview[]}
        country={i18nCountries.getName(specialist?.country_of_birthday ?? '', i18n.language) ?? ''}
        isSpecialistUnAvailable={isSpecialistUnAvailable}
        isAuth={!!authUser}
        onLoadMoreReviews={handleLoadMoreReviews}
        currentUser={currentUser as any}
        hasSessions={hasSession}
        showContract={isShowContract}
        onOpenContract={handleOpenContract}
        showReview={showReview}
        onRateSession={handleRateSession}
        onEditSpecialistReview={onEditSpecialistReview}
        isLoading={isLoading || !specialist}
        events={events}
        areasOfWorkByClientProblem={areasOfWorkByClientProblem}
        isLoadingAreasOfWorkByClientProblem={isLoadingAreasOfWorkByClientProblem}
      />
    );
  }, [
    isLoading,
    specialist,
    reviews,
    authUser,
    isSpecialistUnAvailable,
    hasSession,
    isShowContract,
    handleLoadMoreReviews,
    handleOpenContract,
    handleRateSession,
    handleOpenSuperSpecialistArticle,
    handleOpenNewSpecialistArticle,
    onEditSpecialistReview,
    currentUser,
    specialist,
    showReview,
    handleReviewModalClose,
    events,
    isLoadingAreasOfWorkByClientProblem,
    areasOfWorkByClientProblem,
  ]);

  return (
    <IonPage ref={pageRef}>
      {authUser ? (
        <PageHeader
          showAvatar={false}
          backChevron
          showPromoCodeTimer
          onShare={!showNotFound ? () => setIsShareOpened(true) : undefined}
          backChevronAction={isNeedRedirectToOnboarding ? redirectToOnboarding : undefined}
        >
          <>
            {!showNotFound && (
              <IconButton
                onClick={() => toggleFavourite(specialist!)}
                icon={<IconHeart filled={isFavouriteSpecialist(id)} />}
                className="p-2"
              />
            )}
          </>
        </PageHeader>
      ) : (
        <PageHeader childrenInCenterSlot showAvatar={false}>
          <DownloadAppFeature
            inHeader
            onLoginClick={() => {
              history.push(PublicRoutes.AUTH);
            }}
            onDownloadClick={handleAppDownload}
          />
        </PageHeader>
      )}
      <IonContent>
        {showNotFound ? (
          <Container>
            <div className="flex items-center justify-center h-full">
              <TranslatedSpecialistProfileNotFound isAuth={!!authUser} />
            </div>
          </Container>
        ) : (
          <Container style={{ padding: 0 }} ref={contentRef} scrollEvents>
            {loadingValue || !specialist ? skeleton : widget}
          </Container>
        )}
        <TranslatedShareModalFeature
          isOpen={isShareOpened}
          shareUrl={`${environment.MAIN_WEB_URL}${PublicRoutes.SPECIALIST_BY_SLUG.replace(':slug', specialist?.slug ?? '')}`}
          shareTitle={t('specialists:share_profile.title', {
            specialistName: `${specialist?.first_name} ${specialist?.last_name?.[0]}.`,
          })}
          shareText={t('specialists:share_profile.title', {
            specialistName: `${specialist?.first_name} ${specialist?.last_name?.[0]}.`,
          })}
          onDidDismiss={() => setIsShareOpened(false)}
          onShare={() => setIsShareOpened(false)}
          onLinkCopied={() => {
            setIsShareOpened(false);
            showToast({ text: t('app:profile_link.copied_tost') });
          }}
        />

        <CardModal
          presentingElement={schedulePresentingElement.current ? schedulePresentingElement.current : undefined}
          isOpen={isSignAgreementOpen && currentOpenModal === 'schedule'}
          onDidDismiss={closeModal}
        >
          <SignAgreementWidget
            type={!specialist?.client?.specialist_client_contract_id ? 'new' : 'last'}
            specialistId={specialist?.id ?? ''}
            // @ts-ignore
            contractStatus={clientSpecialistContractStatus}
            isLoading={fetchStatus === 'pending'}
            onApply={handleApplyContract}
          />
        </CardModal>
        <CardModal
          isOpen={isModalReviewOpen}
          presentingElement={schedulePresentingElement.current ? schedulePresentingElement.current : undefined}
          onDidDismiss={handleReviewModalClose}
        >
          <Review
            specialistID={reviewData.specialistID ?? ''}
            specialistName={reviewData.specialistName ?? ''}
            avatar={reviewData.avatar ?? ''}
            onClose={handleReviewModalClose}
          />
        </CardModal>
        {specialist && (
          <ConfirmAppointmentFeature
            ref={confirmAppointmentRef}
            specialist={specialist as unknown as Specialist}
            selectedSlot={selectedSlot}
            onClose={() => setSelectedSlot(undefined)}
          />
        )}
      </IonContent>
      {authUser && !showNotFound && (
        <AppointmentFooter
          specialistFullName={`${specialist?.first_name} ${specialist?.last_name ? `${specialist?.last_name?.[0]}.` : ''}`}
          specialistID={id}
          loading={loadingValue}
          countConsultationFor3Days={newProfile ? undefined : specialist?.session_count_by_3_days}
          presentingElement={pageRef.current}
        />
      )}
    </IonPage>
  );
};

export default withSpecialistScheduleContext(SpecialistProfilePage);
